import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineChartLineIcon from 'LuxKit/Icons/line/LineChartLineIcon'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import LineGuidedtoursIcon from 'LuxKit/Icons/line/LineGuidedtoursIcon'
import LineIncomingCallIcon from 'LuxKit/Icons/line/LineIncomingCallIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineUserSquareIcon from 'LuxKit/Icons/line/LineUserSquareIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowSale from 'LuxKit/PricePoints/PriceRowSale'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import TourStyleTag from 'components/OfferPage/TourV2/TourStyleTag'
import Group from 'components/utils/Group'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { TOUR_STYLED_PRODUCT_TYPE_HOTEL_BENEFITS } from 'constants/tours'
import ModalContext from 'contexts/ModalContext'
import ContactContext from 'contexts/contactContext'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { getTourPricePerDay, getTourValueAtPerDay, isTourProductTypeLabelled, isTourProductTypeStyled } from 'lib/tours/tourUtils'
import { isCruiseFlashOfferV2 } from 'lib/offer/offerTypes'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import type { TourDepositInfoModalResult } from './TourDepositInfoModal'
import TourDepositInfoModal from './TourDepositInfoModal'
import TourDepositTag from './TourDepositTag'
import TourV2GuideTypeToolTipInfo from './TourV2GuideTypeToolTipInfo'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import { generateLoyaltyPointsCalculatorTourOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { getTourPriceMode } from 'selectors/TourV2/toursSelectors'
import AgentCommissionPanel from 'agentHub/components/AgentCommissionPanel'

const OverviewPane = styled(Pane)`
  border-radius: ${props => props.theme.borderRadius.XS};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  box-shadow: ${props => props.theme.shadow.flat.small};
  padding: ${rem(20)};
`

const OverviewPaneDetailItemsContainer = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-auto-flow: row;
  grid-template-columns: repeat(2, 1fr);
`

interface Props {
  activityLevel?: string
  purchasableOption?: App.Tours.TourV2OfferPurchasableOption
  isOfferPurchasable: boolean
  variation: App.Tours.TourV2OfferVariation
  productType: App.Tours.V2ProductType
  onCheckAvailability: () => void
  guideType?: string
  offer: App.Tours.TourV2Offer
}

function TourV2OverviewPanel(props: Props) {
  const {
    variation,
    activityLevel,
    purchasableOption,
    isOfferPurchasable,
    onCheckAvailability,
    guideType,
    offer,
  } = props
  const intl = useIntl()
  const personSaleUnit = intl.formatMessage({
    id: 'tJp-Qbrrz863Uecjri0B',
    defaultMessage: 'person',
    description: 'Per person sale unit label in tour overview panel in the tours section',
  })
  const dayPerPersonSaleUnit = intl.formatMessage({
    id: 'rWIhvO2zdFoo_gNwAikh',
    defaultMessage: 'day per person',
    description: 'Per day per person sale unit label in tour overview panel',
  })
  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(purchasableOption)
  const {
    placesCount,
    durationInDaysCount,
    countriesCount,
    maxPax,
    minAge,
    maxAge,
    daysCount,
  } = useTourV2VariationDetails(variation)
  const tourPriceMode = useAppSelector(getTourPriceMode)
  const pricePerDayDetails = useMemo(() => {
    if (!purchasableOptionDetails || tourPriceMode === 'total') return
    return {
      valueAtPerDay: getTourValueAtPerDay(purchasableOptionDetails.valuedAt?.price, daysCount),
      pricePerDay: getTourPricePerDay(purchasableOptionDetails.pricePerPerson, purchasableOptionDetails.memberPricePerPerson, daysCount),
    }
  }, [purchasableOptionDetails, daysCount, tourPriceMode])
  const { defaultContact } = useContext(ContactContext)
  const isLuxPlusMember = useAppSelector(getCanViewLuxPlusBenefits)
  const isCruiseFlashOffer = isCruiseFlashOfferV2(offer)

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.toursRequestAgentCallbackEnabled)

  const showModal = useContext(ModalContext)
  const handleCallbackClick = () => { showModal(<RequestAgentCallbackModal type="TOURS" />) }

  let hotelBenefit = isTourProductTypeStyled(offer.productType) ? TOUR_STYLED_PRODUCT_TYPE_HOTEL_BENEFITS[offer.productType] : undefined
  if (['tour-e79792ac-e43e-4de0-b682-fa4a1952518c', 'tour-0206690e-757b-45e5-be63-d9601a648555', 'tour-4589224f-34f4-4489-a74c-31002636b587', 'tour-33df896e-c813-4366-8a4f-2d4ff36230d3', 'tour-f3bab132-3a44-49bd-9b18-c1b9e98f7d03'].includes(offer.id)) {
    hotelBenefit = '4 & 5 star hotels'
  }
  if (['tour-1982613d-9b7f-43d1-b4e4-6facc7db8d88'].includes(offer.id)) {
    hotelBenefit = '4 & 4.5 star hotels'
  }

  const pointsEarnCalculationOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(
    () => {
      const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(offer)
      const nights = daysCount && daysCount > 0 ? daysCount - 1 : undefined
      return [
        generateLoyaltyPointsCalculatorTourOptions(offer,
          {
            price: cheapestPurchasableOption?.price,
            memberPrice: cheapestPurchasableOption?.memberPrice,
            roomType: cheapestPurchasableOption?.roomType,
            packageId: cheapestPurchasableOption?.fkDepartureId,
            offerId: cheapestPurchasableOption?.fkTourId,
            nights,
          }),
      ]
    },
    [offer, daysCount],
  )

  const handleTourDepositInfoClick = useCallback(async() => {
    if (offer.paymentScheduleTemplate) {
      const result = await showModal<TourDepositInfoModalResult>(<TourDepositInfoModal
        paymentScheduleTemplate={offer.paymentScheduleTemplate}
        primaryActionText={intl.formatMessage({
          id: '8jowxjaBSFwrFBgdcnmz',
          defaultMessage: 'Check Availability',
          description: 'Action button label to check availability in the tours section',
        })}
      />)
      if (result === 'primary-action') {
        onCheckAvailability()
      }
    }
  }, [offer.paymentScheduleTemplate, onCheckAvailability, showModal, intl])

  const shouldTrackPrice = !!(purchasableOptionDetails?.pricePerPerson)
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: purchasableOptionDetails.pricePerPerson.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, purchasableOptionDetails?.pricePerPerson, offer?.id])

  return <OverviewPane>
    <Group direction="vertical" gap={16}>
      <div>
        <Heading variant="heading5">
          {intl.formatMessage({
            id: 'qZZQorca5wxtuEpauJeQ',
            defaultMessage: 'Overview',
            description: 'Section heading for the overview content area on the offer or property detail page',
          })}
        </Heading>
        {isTourProductTypeStyled(offer.productType) && isTourProductTypeLabelled(offer.productType) && <TourStyleTag
          productType={offer.productType}
          onCheckAvailability={onCheckAvailability}
        />}
      </div>
      <OverviewPaneDetailItemsContainer>
        <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineClockIcon />}>
          {durationInDaysCount}
        </BodyTextBlock>
        {placesCount && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineMapIcon />}>
          {placesCount}
        </BodyTextBlock>}
        {countriesCount && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineGlobeIcon />}>
          {countriesCount}
        </BodyTextBlock>}
        {maxPax && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineUsersAltIcon />}>
          {intl.formatMessage(
            {
              id: '2ySHWgTupYrxjiJz_0ya',
              defaultMessage: '{count} people max.',
              description: 'Label displaying the maximum number of people allowed for a room or experience',
            },
            { count: maxPax },
          )}
        </BodyTextBlock>}
        {minAge && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineUserSquareIcon />}>
          {(() => {
            if (!maxAge) {
              return intl.formatMessage(
                {
                  id: 'DxnMjojt4eb-nYm9FcfR',
                  defaultMessage: '{age}+ yrs',
                  description: 'Label displaying the minimum age requirement in years for a tour or experience',
                },
                { age: minAge },
              )
            }
            return intl.formatMessage(
              {
                id: '3CRzF4FfojlBwwUzwYoF',
                defaultMessage: '{minAge} - {maxAge} yrs',
                description: 'Label displaying the age range in years for traveller categories on the tour page',
              },
              { minAge, maxAge },
            )
          })()}
        </BodyTextBlock>}
        {activityLevel && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineChartLineIcon />} format="titlecase">
          {activityLevel}
        </BodyTextBlock>}
        {guideType && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineGuidedtoursIcon />} endIcon={<TourV2GuideTypeToolTipInfo guideType={guideType} />}>
          {guideType}
        </BodyTextBlock>}
        {offer.privateJourney && !isCruiseFlashOffer && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineUsersAltIcon />}>
          {intl.formatMessage({
            id: 'BG7fLhuTf1g49XlDaIEx',
            defaultMessage: 'Private tour',
            description: 'Badge label indicating that a tour offer is a private tour experience',
          })}
        </BodyTextBlock>}
        {!!hotelBenefit && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineBedIcon />}>
          {hotelBenefit}
        </BodyTextBlock>}
      </OverviewPaneDetailItemsContainer>
      <Divider direction="horizontal" kind="secondary" />
      <div>
        {!!purchasableOptionDetails && <VerticalSpacer gap={4}>
          <div>
            {purchasableOptionDetails.pricePerPerson && (
              <VerticalSpacer gap={8}>
                {offer.paymentScheduleTemplate && <TourDepositTag offer={offer} onInfoClick={handleTourDepositInfoClick} />}
                <div>
                  <PriceRowPriceCaption>{intl.formatMessage({
                    id: 'avzYXBQTNn8u967aT_q1',
                    defaultMessage: 'From',
                    description: 'Pricing label prefix before tour overview price in the tours section',
                  })}
                  </PriceRowPriceCaption>
                  {!pricePerDayDetails?.pricePerDay && <LuxPlusPriceStack size="L" price={Number(purchasableOptionDetails.pricePerPerson)} saleUnit={personSaleUnit} memberPrice={Number(purchasableOptionDetails.memberPrice)} isTour />}
                  {pricePerDayDetails?.pricePerDay && <LuxPlusPriceStack size="L" price={pricePerDayDetails.pricePerDay.pricePerPersonPerDay} saleUnit={dayPerPersonSaleUnit} memberPrice={pricePerDayDetails.pricePerDay.memberPricePerPersonPerDay} isTour />}
                </div>
              </VerticalSpacer>
            )}
            {purchasableOptionDetails.hasDiscount && <PriceRowSale value={purchasableOptionDetails.fullPricePerPerson} />}
            {!!purchasableOptionDetails.roomTypeName && <PriceRowCaption>{purchasableOptionDetails.roomTypeName}</PriceRowCaption>}
            {
              !!purchasableOptionDetails.valuedAt && <>
                {!pricePerDayDetails?.valueAtPerDay && <PriceRowValueDiscountWithCaption
                  size="M"
                  originalValue={purchasableOptionDetails.valuedAt.price}
                  discountPercentage={isLuxPlusMember ? purchasableOptionDetails.valuedAt.memberSavingsPercentage : purchasableOptionDetails.valuedAt.savingPercentage}
                />}
                {!!pricePerDayDetails?.valueAtPerDay && <PriceRowValueDiscountWithCaption
                  size="M"
                  originalValue={pricePerDayDetails?.valueAtPerDay}
                  discountPercentage={isLuxPlusMember ? purchasableOptionDetails.valuedAt.memberSavingsPercentage : purchasableOptionDetails.valuedAt.savingPercentage}
                />}
              </>
            }

          </div>
          <AgentCommissionPanel
            offerId={offer.id}
            offerTotal={purchasableOptionDetails.pricePerPerson ?? 0}
            retailValue={purchasableOptionDetails.valuedAt?.price}
            memberPrice={purchasableOptionDetails.memberPrice ?? undefined}
          />
        </VerticalSpacer>}
        {!isOfferPurchasable && <Heading variant="heading5" align="center">
          {intl.formatMessage({
            id: 'dCETjSJ5TlDQR2bRitEB',
            defaultMessage: 'This offer has sold out',
            description: 'Status text when offer is sold out on the offer listing or detail page',
          })}
        </Heading>}
      </div>
      <LoyaltyPointsEarn
        calculationRequests={pointsEarnCalculationOptions}
        calculationType="estimate"
        offer={offer}
      />
      <Group direction="vertical" gap={8}>
        {isOfferPurchasable && <TextButton kind="primary" fit="full-width" onClick={onCheckAvailability}>
          {intl.formatMessage({
            id: '8jowxjaBSFwrFBgdcnmz',
            defaultMessage: 'Check Availability',
            description: 'Action button label to check availability in the tours section',
          })}
        </TextButton>}
        {!isOfferPurchasable && <TextButton kind="secondary" to="/tours">
          {intl.formatMessage({
            id: 'K8g_FjvoRmrRbbpozk2N',
            defaultMessage: 'Browse all tour offers',
            description: 'Button text to browse all tour offers on the tour offer detail page',
          })}
        </TextButton>}
        <SoftOfferPageWalledGarden>
          <TextButton
            fit="full-width"
            kind="secondary"
            href={`tel:${defaultContact.number}`}
          >
            {intl.formatMessage(
              {
                id: 'YSkMPU66SoatWGRB0lGb',
                defaultMessage: 'Call {contactNumber}',
                description: 'Button text with the phone number to call the customer support contact directly',
              },
              { contactNumber: defaultContact.humanReadable },
            )}
          </TextButton>
          {callbacksFeatureEnabled && <TextButton
            fit="full-width"
            kind="secondary"
            onClick={handleCallbackClick}
            startIcon={<LineIncomingCallIcon />}
          >
            {intl.formatMessage({
              id: 'zcL3CBQk4hS7NFHhYuk0',
              defaultMessage: 'Request a callback',
              description: 'Action button label to request callback in the tours section',
            })}
          </TextButton>}
        </SoftOfferPageWalledGarden>
      </Group>
    </Group>
  </OverviewPane>
}

export default TourV2OverviewPanel
